import { useState, useCallback, useRef } from 'react';
import { sendAgentMessage, ChatMessage, AgentChatResponse } from '../lib/agent-api';

export interface ChatEntry {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	toolsUsed?: string[];
	guardrailFlags?: string[];
	timestamp: Date;
}

interface UseAgentChatOptions {
	baseUrl: string;
	token: string;
	maxHistoryLength?: number;
}

export function useAgentChat({ baseUrl, token, maxHistoryLength = 20 }: UseAgentChatOptions) {
	const [messages, setMessages] = useState<ChatEntry[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const historyRef = useRef<ChatMessage[]>([]);

	const sendMessage = useCallback(
		async (text: string) => {
			setError(null);
			setIsLoading(true);

			const userEntry: ChatEntry = {
				id: `msg-${Date.now()}-user`,
				role: 'user',
				content: text,
				timestamp: new Date(),
			};
			setMessages((prev) => [...prev, userEntry]);

			try {
				const response: AgentChatResponse = await sendAgentMessage(baseUrl, token, {
					message: text,
					conversationHistory: historyRef.current,
				});

				historyRef.current = [
					...historyRef.current,
					{ role: 'user' as const, content: text },
					{ role: 'assistant' as const, content: response.response },
				].slice(-maxHistoryLength);

				const assistantEntry: ChatEntry = {
					id: `msg-${Date.now()}-assistant`,
					role: 'assistant',
					content: response.response,
					toolsUsed: response.toolsUsed,
					guardrailFlags: response.guardrailFlags,
					timestamp: new Date(),
				};
				setMessages((prev) => [...prev, assistantEntry]);
			} catch (err: any) {
				setError(err.message || 'An unexpected error occurred');
			} finally {
				setIsLoading(false);
			}
		},
		[baseUrl, token, maxHistoryLength]
	);

	const clearChat = useCallback(() => {
		setMessages([]);
		historyRef.current = [];
		setError(null);
	}, []);

	return { messages, isLoading, error, sendMessage, clearChat };
}
